import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, MessageCircle, Repeat2, Bookmark, Star,
  Clock, ChevronDown, ChevronUp, CheckCircle, Flame,
  MoreVertical, Pencil, Trash2
} from 'lucide-react';
import { RecipePost, foodCommunityService, getTagLabel, getTagColor, formatTimeAgo } from '../../services/foodCommunityService';

interface RecipePostCardProps {
  post: RecipePost;
  onUpdate?: (updated: RecipePost) => void;
  onDelete?: (postId: string) => void;
  compact?: boolean;
  currentUserId?: string;
}

export const RecipePostCard: React.FC<RecipePostCardProps> = ({ post, onUpdate, onDelete, compact = false, currentUserId }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [localPost, setLocalPost] = useState(post);
  const [isLiking, setIsLiking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isOwner = currentUserId && localPost.userId === currentUserId;

  // Close menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);

  const userName = localPost.user
    ? `${localPost.user.firstName || ''} ${localPost.user.lastName || ''}`.trim()
    : 'Utilizator';

  const initials = userName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLiking) return;
    setIsLiking(true);
    const prev = { ...localPost };
    // Optimistic update
    setLocalPost(p => ({
      ...p,
      isLiked: !p.isLiked,
      likesCount: p.isLiked ? p.likesCount - 1 : p.likesCount + 1,
    }));
    try {
      await foodCommunityService.toggleLike(localPost.id);
      onUpdate?.({ ...localPost, isLiked: !localPost.isLiked });
    } catch {
      setLocalPost(prev);
    } finally {
      setIsLiking(false);
    }
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSaving) return;
    setIsSaving(true);
    const prev = { ...localPost };
    setLocalPost(p => ({
      ...p,
      isSaved: !p.isSaved,
      savesCount: p.isSaved ? p.savesCount - 1 : p.savesCount + 1,
    }));
    try {
      await foodCommunityService.toggleSave(localPost.id);
      onUpdate?.({ ...localPost, isSaved: !localPost.isSaved });
    } catch {
      setLocalPost(prev);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRate = async (e: React.MouseEvent, rating: number) => {
    e.stopPropagation();
    try {
      const result = await foodCommunityService.ratePost(localPost.id, rating);
      setLocalPost(p => ({
        ...p,
        userRating: rating,
        averageRating: result.averageRating,
        ratingsCount: p.userRating ? p.ratingsCount : p.ratingsCount + 1,
      }));
      setShowRating(false);
    } catch {}
  };

  const handleCardClick = () => {
    navigate(`/food-community/post/${localPost.id}`);
  };

  const allTags = [...localPost.tags, ...localPost.customTags];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate(`/food-community/user/${localPost.userId}`)}
        >
          {localPost.user?.profilePhotoUrl ? (
            <img
              src={localPost.user.profilePhotoUrl}
              alt={userName}
              className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center text-white font-semibold text-sm">
              {initials}
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900 text-sm">{userName}</span>
              {localPost.user?.userType === 'SPECIALIST' && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                  Nutritionist
                </span>
              )}
            </div>
            <span className="text-xs text-gray-400">{formatTimeAgo(localPost.createdAt)}</span>
          </div>
        </div>

        {/* Badges + 3-dot menu */}
        <div className="flex items-center gap-2">
          {localPost.isValidatedByNutritionist && (
            <span className="flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded-full font-medium">
              <CheckCircle size={11} />
              Validat
            </span>
          )}
          {localPost.calories && (
            <span className="flex items-center gap-1 text-xs bg-orange-50 text-orange-600 border border-orange-100 px-2 py-1 rounded-full font-semibold">
              <Flame size={11} />
              {Math.round(localPost.calories)} kcal
            </span>
          )}

          {/* 3-dot menu - only for post owner */}
          {isOwner && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              >
                <MoreVertical size={16} />
              </button>

              {showMenu && (
                <div className="absolute right-0 top-9 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50 min-w-[140px]">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      navigate(`/food-community/post/${localPost.id}/edit`);
                    }}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Pencil size={14} className="text-gray-400" />
                    Editeaza
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      if (!window.confirm('Esti sigur ca vrei sa stergi aceasta postare?')) return;
                      setDeleting(true);
                      try {
                        await foodCommunityService.deletePost(localPost.id);
                        onDelete?.(localPost.id);
                      } catch {}
                      setDeleting(false);
                    }}
                    disabled={deleting}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={14} />
                    {deleting ? 'Se sterge...' : 'Sterge'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image */}
      {localPost.imageUrl && (
        <div
          className="relative cursor-pointer overflow-hidden w-full"
          style={{ paddingBottom: compact ? '56.25%' : '75%' }}
          onClick={handleCardClick}
        >
          <img
            src={localPost.imageUrl}
            alt={localPost.title}
            className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
      )}

      {/* Content */}
      <div className="px-4 pt-3 pb-2">
        <h3
          className="font-bold text-gray-900 text-base leading-snug cursor-pointer hover:text-green-700 transition-colors"
          onClick={handleCardClick}
        >
          {localPost.title}
        </h3>

        {localPost.description && (
          <p className="text-gray-500 text-sm mt-1 leading-relaxed line-clamp-2">
            {localPost.description}
          </p>
        )}

        {/* Tags */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {allTags.slice(0, 4).map(tag => (
              <span
                key={tag}
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${getTagColor(tag)}`}
              >
                {getTagLabel(tag)}
              </span>
            ))}
            {allTags.length > 4 && (
              <span className="text-xs text-gray-400 px-1">+{allTags.length - 4}</span>
            )}
          </div>
        )}

        {/* Macros Bar */}
        {(localPost.protein || localPost.carbs || localPost.fats || localPost.prepTime) && (
          <div className="flex items-center gap-4 mt-3 py-2 border-t border-gray-50">
            {localPost.protein != null && (
              <div className="text-center">
                <div className="text-sm font-bold text-green-600">{Math.round(localPost.protein)}g</div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">Proteine</div>
              </div>
            )}
            {localPost.carbs != null && (
              <div className="text-center">
                <div className="text-sm font-bold text-blue-600">{Math.round(localPost.carbs)}g</div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">Carbohidrati</div>
              </div>
            )}
            {localPost.fats != null && (
              <div className="text-center">
                <div className="text-sm font-bold text-amber-600">{Math.round(localPost.fats)}g</div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">Grasimi</div>
              </div>
            )}
            {localPost.prepTime && (
              <div className="text-center ml-auto">
                <div className="flex items-center gap-1 text-sm font-bold text-gray-600">
                  <Clock size={13} />
                  {localPost.prepTime}m
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-wide">Timp</div>
              </div>
            )}
          </div>
        )}

        {/* Rating */}
        {localPost.ratingsCount > 0 && (
          <div className="flex items-center gap-2 mt-2">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map(s => (
                <Star
                  key={s}
                  size={13}
                  className={s <= Math.round(localPost.averageRating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}
                />
              ))}
            </div>
            <span className="text-sm font-semibold text-gray-700">{localPost.averageRating?.toFixed(1)}</span>
            <span className="text-xs text-gray-400">({localPost.ratingsCount})</span>
            {!localPost.userRating && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowRating(!showRating); }}
                className="ml-auto text-xs text-green-600 font-medium hover:text-green-700"
              >
                Evalueaza
              </button>
            )}
          </div>
        )}

        {/* Inline Rating Stars */}
        {showRating && (
          <div className="flex items-center gap-1 mt-2 p-2 bg-gray-50 rounded-xl">
            <span className="text-xs text-gray-500 mr-1">Rating:</span>
            {[1, 2, 3, 4, 5].map(s => (
              <button
                key={s}
                onMouseEnter={() => setHoverRating(s)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={(e) => handleRate(e, s)}
                className="p-0.5"
              >
                <Star
                  size={20}
                  className={s <= (hoverRating || localPost.userRating || 0)
                    ? 'text-amber-400 fill-amber-400'
                    : 'text-gray-300 fill-gray-300'}
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-gray-50">
        <div className="flex items-center gap-4">
          {/* Like */}
          <button
            onClick={handleLike}
            className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
              localPost.isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-400'
            }`}
          >
            <Heart
              size={18}
              className={localPost.isLiked ? 'fill-red-500' : ''}
            />
            <span>{localPost.likesCount > 0 ? localPost.likesCount : ''}</span>
          </button>

          {/* Comments */}
          <button
            onClick={handleCardClick}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-blue-500 transition-colors"
          >
            <MessageCircle size={18} />
            <span>{localPost.commentsCount > 0 ? localPost.commentsCount : ''}</span>
          </button>

          {/* Repost */}
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/food-community/post/${localPost.id}?repost=1`); }}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-green-500 transition-colors"
          >
            <Repeat2 size={18} />
            <span>{localPost.repostsCount > 0 ? localPost.repostsCount : ''}</span>
          </button>
        </div>

        <div className="flex items-center gap-3">
          {/* Save */}
          <button
            onClick={handleSave}
            className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
              localPost.isSaved ? 'text-green-600' : 'text-gray-500 hover:text-green-500'
            }`}
          >
            <Bookmark
              size={18}
              className={localPost.isSaved ? 'fill-green-600' : ''}
            />
          </button>

          {/* Expand Recipe */}
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-full transition-colors"
          >
            Reteta
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        </div>
      </div>

      {/* Expanded Recipe */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-50 bg-gray-50/50">
          {localPost.ingredients && localPost.ingredients.length > 0 && (
            <div className="mt-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Ingrediente</h4>
              <ul className="space-y-1">
                {localPost.ingredients.map((ing, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                    <span className="font-medium">{ing.amount} {ing.unit}</span>
                    <span>{ing.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {localPost.instructions && (
            <div className="mt-3">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Mod de preparare</h4>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                {localPost.instructions}
              </p>
            </div>
          )}

          <button
            onClick={handleCardClick}
            className="mt-3 w-full text-center text-sm text-green-600 font-medium hover:text-green-700"
          >
            Vezi reteta completa
          </button>
        </div>
      )}
    </div>
  );
};
